import dayjs, { Dayjs } from 'dayjs';
import { orderStatus } from '../utils/order/orderStatus';
import { InstructionType } from './bundleTypes';

export type reqStatus =
  | 'REQUEST::INIT'
  | 'REQUEST::PENDING'
  | 'REQUEST::ERROR'
  | 'REQUEST::SUCCESS';

//================ Address representations ==========================

const buildWindowTime = (from: Dayjs, to: Dayjs): string => {
  return `${from.format('HH:mm')} - ${to.format('HH:mm')}`;
};

const buildTimeInterval = (from: Dayjs, to: Dayjs): TimeInterval => {
  return { from, to };
};

export interface DeliveryAddress {
  address: string;
  name: string;
  phone: string;
  latitude: number;
  longitude: number;
}

export interface Address {
  streetName?: string;
  streetNumber?: string;
  postalCode: string;
  city?: string;
  country: string;
}

export enum AddressType {
  NORMAL = 'NORMAL',
  HUB = 'HUB',
}

// TODO can this replace Address?
export interface InternalAddress extends Address {
  location: Location;
  addressType: AddressType;
  addressValidity: AddressValidity;
  fullAddressString: string;
}

export enum AddressValidity {
  VALID = 'VALID',
  NEEDS_MANUAL_VALIDATION = 'NEEDS_MANUAL_VALIDATION',
  CONFIRMED = 'CONFIRMED',
}

export enum Zone {
  OSLO_CORE = 'OSLO_CORE',
  AKERSHUS_EXTENDED = 'AKERSHUS_EXTENDED',
  BERGEN_CORE = 'BERGEN_CORE',
  BERGEN_EXTENDED = 'BERGEN_EXTENDED',
  VESTFOLD = 'VESTFOLD',
  STAVANGER_CORE = 'STAVANGER_CORE',
  STAVANGER_EXTENDED = 'STAVANGER_EXTENDED',
  TRONDHEIM_CORE = 'TRONDHEIM_CORE',
  TRONDHEIM_EXTENDED = 'TRONDHEIM_EXTENDED',
  GRENLAND = 'GRENLAND',
  OSTFOLD = 'OSTFOLD',
  KRISTIANSAND = 'KRISTIANSAND',
}

export enum GeoZone {
  UNKNOWN = '',
  OSLO_BAERUM = 'OB',
  NORTH = 'ON',
  EAST = 'OE',
  WEST = 'OW',
  OSLO_A = 'OSL-A',
  OSLO_B = 'OSL-B',
  OSLO_C = 'OSL-C',
  OSLO_D = 'OSL-D',
  OSLO_E = 'OSL-E',
  OSLO_F = 'OSL-F',
  OSLO_G = 'OSL-G',
  OSLO_H = 'OSL-H',
  BERGEN = 'B',
  VESTFOLD = 'V',
  STAVANGER = 'S',
  TRONDHEIM = 'T',
  GRENLAND = 'G',
  KRISTIANSAND = 'K',
}

export enum Region {
  OSLO = 'OSLO',
  VESTFOLD = 'VESTFOLD',
  BERGEN = 'BERGEN',
  STAVANGER = 'STAVANGER',
  TRONDHEIM = 'TRONDHEIM',
  GRENLAND = 'GRENLAND',
  OSTFOLD = 'OSTFOLD',
  KRISTIANSAND = 'KRISTIANSAND',
}

export interface RegionConfigApi {
  region: Region;
  hubAddress: InternalAddress;
  temperatureControlEnabled: boolean;
  handoverPointAddresses: InternalAddress[];
}

export enum Country {
  NORWAY = 'no',
  SWEDEN = 'se',
}

export const RegionsList = [
  Region.OSLO,
  Region.VESTFOLD,
  Region.BERGEN,
  Region.STAVANGER,
  Region.TRONDHEIM,
  Region.GRENLAND,
  Region.OSTFOLD,
  Region.KRISTIANSAND,
];

export const RegionCode: { [region in Region]: string } = {
  OSLO: 'OSL',
  TRONDHEIM: 'TRD',
  STAVANGER: 'SVG',
  BERGEN: 'BGO',
  VESTFOLD: 'VFO',
  GRENLAND: 'GRE',
  OSTFOLD: 'ØFO',
  KRISTIANSAND: 'KRS',
};

export function convertGeoZoneToRegion(geoZoneString: string): Region {
  switch (geoZoneString) {
    case 'V':
      return Region.VESTFOLD;
    case 'B':
      return Region.BERGEN;
    case 'S':
      return Region.STAVANGER;
    case 'T':
      return Region.TRONDHEIM;
    case 'G':
      return Region.GRENLAND;
    case 'Ø':
      return Region.OSTFOLD;
    case 'K':
      return Region.KRISTIANSAND;
    case 'OB':
    case 'ON':
    case 'OE':
    case 'OW':
    case 'OSL-A':
    case 'OSL-B':
    case 'OSL-C':
    case 'OSL-D':
    case 'OSL-E':
    case 'OSL-F':
    case 'OSL-G':
    case 'OSL-H':
    default:
      return Region.OSLO;
  }
}

export const middleOfTheWorld = { lat: 60.535947, lng: 8.2073158 };

//================ Order representations ==========================
enum OrderCommunicationEventType {
  EMAIL_RECIPIENT_ORDER_CREATED = 'EMAIL_RECIPIENT_ORDER_CREATED',
  SMS_RECIPIENT_ORDER_CREATED = 'SMS_RECIPIENT_ORDER_CREATED',
}

export interface OrderCommunicationEvent {
  eventType: OrderCommunicationEventType;
  time: string;
}

export const zoneNames: { [zone in Zone]: string } = {
  OSLO_CORE: 'Oslo',
  AKERSHUS_EXTENDED: 'Viken',
  BERGEN_CORE: 'Bergen Core',
  BERGEN_EXTENDED: 'Bergen Extended',
  VESTFOLD: 'Vestfold',
  STAVANGER_CORE: 'Stavanger Core',
  STAVANGER_EXTENDED: 'Stavanger Extended',
  TRONDHEIM_CORE: 'Trondheim Core',
  TRONDHEIM_EXTENDED: 'Trondheim Extended',
  GRENLAND: 'Grenland',
  OSTFOLD: 'Østfold',
  KRISTIANSAND: 'Kristiansand',
};

export const regionNames: { [region in Region]: string } = {
  OSLO: 'OSLO',
  VESTFOLD: 'VESTFOLD',
  BERGEN: 'BERGEN',
  STAVANGER: 'STAVANGER',
  TRONDHEIM: 'TRONDHEIM',
  GRENLAND: 'GRENLAND',
  OSTFOLD: 'ØSTFOLD',
  KRISTIANSAND: 'KRISTIANSAND',
};

export class OrderMonitor {
  confirmationPin: string;
  deliverFrom: string;
  deliverUntil: string;
  deliveredAt?: string;
  deviations?: Deviations[];
  courierDeviations?: Deviations[];
  destinationAddress?: InternalAddress;
  geoZone: GeoZone;
  geoCode: '' | 'OSL' | 'VFO' | 'BGO' | 'SVG' | 'TRD';
  id: string;
  openCourierDeviation: boolean;
  originAddress: InternalAddress;
  pickedAt?: string;
  pickupEmail: string;
  pickupName: string;
  pickupPhone: string; // TODO to be removed
  pickupPhoneCountryCode: string;
  pickupPhoneNumber: string;
  pickupPin: string;
  recipientAddress: string;
  recipientEmail: string;
  recipientLatitude: number;
  recipientLongitude: number;
  recipientName: string;
  recipientPhone: string; // TODO to be removed
  recipientPhoneCountryCode: string;
  recipientPhoneNumber: string;
  region?: Region;
  senderName?: string;
  status: OrderStatus;
  temperatureControlled?: boolean;
  product?: string;
  idWithListNumber?: string;
}

export class Order {
  chatroomToken?: string;
  confirmationPin: string;
  courierId?: number;
  courierName?: string;
  created: string;
  createdDate: string;
  deliveredAt?: string;
  deliverFrom: string;
  deliverUntil: string;
  deliveryConfirmedLocation?: DeliveryConfirmedLocation;
  deliveryDate: string;
  deliveryId: string;
  deliveryType: OrderDeliveryType;
  deviations?: Deviations[];
  courierDeviations?: Deviations[];
  destinationAddress?: InternalAddress;
  geoZone: GeoZone;
  geoCode: '' | 'OSL' | 'VFO' | 'BGO' | 'SVG' | 'TRD';
  id: string;
  leaveAtDoorstep: boolean;
  noteForCourier: string;
  numberOfParcels: number;
  openCourierDeviation: boolean;
  orderCommunicationEvents: OrderCommunicationEvent[];
  orderReference?: string;
  originAddress: InternalAddress;
  warehouseAddress?: InternalAddress;
  packageSize: OrderPackageSize;
  packagesNumber: number;
  parcels: Parcel[];
  performedVerifications: {
    signatureVerified: boolean;
    confirmationPhoto: boolean;
    idCheckName?: string;
    ageVerified?: number;
  };
  pickedAt?: string;
  pickupEmail: string;
  pickupName: string;
  pickupPhone: string; // TODO to be removed
  pickupPhoneCountryCode: string;
  pickupPhoneNumber: string;
  pickupPin: string;
  recipientAddress: string;
  recipientAddressValidity: AddressValidity;
  recipientCity?: string;
  recipientCountry?: string;
  recipientEmail: string;
  recipientLatitude: number;
  recipientLongitude: number;
  recipientName: string;
  recipientPhone: string; // TODO to be removed
  recipientPhoneCountryCode: string;
  recipientPhoneNumber: string;
  recipientPostalCode?: string;
  recipientStreetName?: string;
  recipientStreetNumber?: string;
  region?: Region;
  scannedAtHub: string;
  scannedAtPartner: string;
  senderEmail?: string;
  senderName?: string;
  status: OrderStatus;
  temperatureControlled?: boolean;
  product?: ProductType;
  technicalNotes: string;
  token: string;
  totalWeightOfParcelsGrams: number;
  verifications: Verifications;
  warehouseRegion: Region;
  xxl: boolean;
  zone: string;
  price?: {
    fractionalDenomination?: number;
    currency?: string;
  };
  accountId?: number;
  pickupDate?: string;
  bundleId?: string;
  brand: ServiceBrand;
  idCheck?: boolean;
  timeWasChanged?: boolean;
  idWithListNumber?: string;
}

export interface TimeInterval {
  from: Dayjs;
  to: Dayjs;
}

export class OrderClazz extends Order {
  static readonly GeoZoneDescriptions: { [geoZone in GeoZone]: string } = {
    '': 'Unknown',
    [GeoZone.OSLO_BAERUM]: 'Oslo-Bærum',
    [GeoZone.NORTH]: 'Oslo North',
    [GeoZone.EAST]: 'Oslo East',
    [GeoZone.WEST]: 'Oslo West',
    [GeoZone.BERGEN]: 'Bergen',
    [GeoZone.OSLO_A]: 'Oslo A',
    [GeoZone.OSLO_B]: 'Oslo B',
    [GeoZone.OSLO_C]: 'Oslo C',
    [GeoZone.OSLO_D]: 'Oslo D',
    [GeoZone.OSLO_E]: 'Oslo E',
    [GeoZone.OSLO_F]: 'Oslo F',
    [GeoZone.OSLO_G]: 'Oslo G',
    [GeoZone.OSLO_H]: 'Oslo H',
    [GeoZone.VESTFOLD]: 'Vestfold',
    [GeoZone.STAVANGER]: 'Stavanger',
    [GeoZone.TRONDHEIM]: 'Trondheim',
    [GeoZone.GRENLAND]: 'Grenland',
    [GeoZone.KRISTIANSAND]: 'Kristiansand',
  };

  static readonly GeoZoneIds: { [geoZone in GeoZone]: string } = {
    '': 'Unknown',
    [GeoZone.OSLO_BAERUM]: 'OB',
    [GeoZone.NORTH]: 'ON',
    [GeoZone.EAST]: 'OE',
    [GeoZone.WEST]: 'OW',
    [GeoZone.BERGEN]: 'B',
    [GeoZone.OSLO_A]: 'OSL-A',
    [GeoZone.OSLO_B]: 'OSL-B',
    [GeoZone.OSLO_C]: 'OSL-C',
    [GeoZone.OSLO_D]: 'OSL-D',
    [GeoZone.OSLO_E]: 'OSL-E',
    [GeoZone.OSLO_F]: 'OSL-F',
    [GeoZone.OSLO_G]: 'OSL-G',
    [GeoZone.OSLO_H]: 'OSL-H',
    [GeoZone.VESTFOLD]: 'V',
    [GeoZone.STAVANGER]: 'S',
    [GeoZone.TRONDHEIM]: 'T',
    [GeoZone.GRENLAND]: 'G',
    [GeoZone.KRISTIANSAND]: 'K',
  };

  readonly deliverFromDate: Dayjs;
  readonly deliverUntilDate: Dayjs;
  readonly createdDateDate: Dayjs;
  readonly numberOfOverSizedParcels: number;
  private readonly statusLabel: string;

  constructor(order: Order, orderChanges?: Partial<Order>) {
    super();
    if (!order) {
      throw Error(
        'Could not create OrderClazz as the source order is undefined'
      );
    }
    Object.assign(this, order, orderChanges);
    this.createdDateDate = dayjs(this.createdDate);
    this.deliverFromDate = dayjs(this.deliverFrom);
    this.deliverUntilDate = dayjs(this.deliverUntil);
    this.numberOfOverSizedParcels = order.parcels.filter(
      parcel => parcel.scannedSize === 'XXL'
    ).length;
    this.statusLabel = orderStatus[this.status].label;
  }

  clone(orderChanges: Partial<Order>): OrderClazz {
    return new OrderClazz(this, orderChanges);
  }

  durationInHours(): number {
    return this.deliverUntilDate.diff(this.deliverFromDate, 'hour');
  }

  durationInMinutes(): number {
    return this.deliverUntilDate.diff(this.deliverFromDate, 'minute');
  }

  windowTime(): string {
    return buildWindowTime(this.deliverFromDate, this.deliverUntilDate);
  }

  interval(): TimeInterval {
    return buildTimeInterval(this.deliverFromDate, this.deliverUntilDate);
  }

  geoZoneId(): string {
    return OrderClazz.GeoZoneIds[this.geoZone];
  }

  geoZoneDesc(): string {
    return OrderClazz.GeoZoneDescriptions[this.geoZone];
  }

  isToday(now: Dayjs): boolean {
    return this.deliverUntilDate.isSame(now, 'day');
  }

  isAfterNow(now: dayjs.Dayjs): boolean {
    return this.deliverUntilDate.isAfter(now);
  }

  isAfterTomorrow(now: dayjs.Dayjs): boolean {
    return this.deliverUntilDate.isAfter(now.add(2, 'day').startOf('day'));
  }

  isYesterday(now: dayjs.Dayjs): boolean {
    return this.deliverUntilDate.startOf('day').diff(now) === -1;
  }

  isSentFromHub(): boolean {
    return this.originAddress.addressType === AddressType.HUB;
  }

  location(type: InstructionType): google.maps.LatLngLiteral {
    const origin = {
      lat: this.originAddress.location.latitude,
      lng: this.originAddress.location.longitude,
    };
    const recipient = {
      lat: this.recipientLatitude,
      lng: this.recipientLongitude,
    };
    return type === InstructionType.Pickup ? origin : recipient;
  }
}

export enum ServiceBrand {
  PORTERBUDDY = 'porterbuddy',
  INSTABOX_HD = 'instabox-hd',
}

export class OrderListOrder {
  id: string;
  deliveryType: OrderDeliveryType;
  noteForCourier: string;
  deliverFrom: string;
  deliverUntil: string;
  senderName?: string;
  originAddress: InternalAddress;
  destinationAddress?: InternalAddress;
  recipientName: string;
  status: OrderStatus;
  packagesNumber: number;
  xxl: boolean;
  packageSize: OrderPackageSize;
  scannedAtPartner: string;
  scannedAtHub: string;
  pickupName: string;
  pickupPhone: string;
  pickupEmail: string;
  recipientPhone: string;
  recipientEmail: string;
  senderEmail?: string;
  orderReference?: string;
  createdDate: string;
  bundleId?: string;
  temperatureControlled: boolean;
  product: string;
  brand: ServiceBrand;
  region: Region;
}

export class OrderListOrderClazz extends OrderListOrder {
  deliverFromDate: dayjs.Dayjs;
  deliverUntilDate: dayjs.Dayjs;
  createdDateDate: dayjs.Dayjs;

  constructor(order: OrderListOrder) {
    super();
    if (!order) {
      throw Error(
        'Could not create OrderListOrderClazz as the source order is undefined'
      );
    }
    Object.assign(this, order);
    this.createdDateDate = dayjs(this.createdDate);
    this.deliverFromDate = dayjs(this.deliverFrom);
    this.deliverUntilDate = dayjs(this.deliverUntil);
  }

  public isSentFromHub = () => {
    return this.originAddress.addressType === AddressType.HUB;
  };

  isToday(now: Dayjs): boolean {
    return this.deliverUntilDate.isSame(now, 'day');
  }

  isAfterNow(now: dayjs.Dayjs): boolean {
    return this.deliverUntilDate.isAfter(now);
  }

  isBeforeNow(now: dayjs.Dayjs): boolean {
    return this.deliverUntilDate.isBefore(now);
  }

  isTomorrow(now: dayjs.Dayjs): boolean {
    return (
      this.deliverUntilDate.startOf('day').diff(now.startOf('day'), 'day') === 1
    );
  }

  isAfterTomorrow(now: dayjs.Dayjs): boolean {
    return this.deliverUntilDate.isAfter(now.add(2, 'day').startOf('day'));
  }

  windowTime(): string {
    return buildWindowTime(this.deliverFromDate, this.deliverUntilDate);
  }

  durationInHours(): number {
    return this.deliverUntilDate.diff(this.deliverFromDate, 'hour');
  }

  interval(): TimeInterval {
    return buildTimeInterval(this.deliverFromDate, this.deliverUntilDate);
  }
}

export interface ScannedOrder {
  order: Order;
  scannedPackages: number;
}

export interface CreateNewOrder {
  origin: Origin;
  destination: Destination;
  parcels: Parcel[];
  product: 'delivery' | 'large';
  orderReference?: string;
  courierInstructions?: string;
  shipmentIdentifier?: number;
  brand: ServiceBrand;
  accountId: number | undefined;
}

export interface UpdateOrderRequest {
  origin: {
    name: string;
    address: Address;
    email: string;
    phoneCountryCode: string;
    phoneNumber: string;
  };
  destination: {
    name: string;
    address: Address;
    email: string;
    phoneCountryCode: string;
    phoneNumber: string;
    deliveryWindow: {
      start: string;
      end: string;
    };
    verifications: {
      leaveAtDoorstep: boolean;
    };
  };
  warehouse: {
    name: string;
    address: Address;
    email: string;
    phoneCountryCode: string;
    phoneNumber: string;
  };
  courierInstructions: string;
  product: 'EXPRESS' | 'DELIVERY';
  packagesNumber: number;
  packageSize: OrderPackageSize;
}

// ======== Deviations representation ==========================
export enum DeviationsStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export interface Deviations {
  deviationMessage?: string;
  deviationStatus?: DeviationsStatus;
  userId?: number;
  timeOfDeviation?: string;
}

export interface DeviationsWithAuthor {
  authorName?: string;
  deviationMessage?: string;
  deviationStatus?: DeviationsStatus;
  userId?: number;
  timeOfDeviation?: string;
  type?: DeviationTypes;
}

export enum DeviationTypes {
  Partner = 'PARTNER',
  Courier = 'COURIER',
}

export enum OrderPackageSize {
  Small = 'SMALL',
  Medium = 'MEDIUM',
  Large = 'LARGE',
  Xxl = 'XXL',
}

export enum OrderStatus {
  Accepted = 'accepted',
  InDelivery = 'in_delivery',
  Delivered = 'delivered',
  Canceled = 'canceled',
  Ready = 'ready',
}

export enum OrderSearchStatus {
  Active = 'ACTIVE',
  Done = 'DONE',
  Cancelled = 'CANCELLED',
  Delivered = 'DELIVERED',
}

export enum OrderDeliveryType {
  Express = 'EXPRESS',
  Scheduled = 'SCHEDULED',
}

//TODO: Merge product types?
export enum ProductType {
  large = 'large',
  delivery = 'delivery',
}

export interface OrderParcels {
  orderId: string;
  parcels: Parcel[];
}

export enum ScanType {
  Partner = 'PARTNER',
  Hub = 'HUB',
  HubSort = 'HUBSORT',
  Pickup = 'PICKUP',
  Delivery = 'DELIVERY',
  Warehouse = 'WAREHOUSE',
  Presort = 'PRESORT',
  Info = 'INFO',
  Machine = 'MACHINESORT',
}

export interface Parcel {
  description?: string;
  constraints?: ParcelConstraints;
  widthCm?: number;
  heightCm?: number;
  depthCm?: number;
  weightGrams: number;
  volumeCm3?: number;
  parcelShipmentIdentifier?: string;
  volume: number;
  scannedSize?: 'SMALL' | 'MEDIUM' | 'LARGE' | 'XXL';
}

interface Window {
  start: Date;
  end: Date;
}

interface Origin {
  name: string;
  address: Address;
  email: string;
  phoneCountryCode?: string;
  phoneNumber: string;
  pickupWindows: Window[];
}

export enum DeliveryVerification {
  CONTACTLESS = 'CONTACTLESS',
  ALLOW_CONTACTLESS = 'ALLOW_CONTACTLESS',
  REQUIRE_PIN = 'REQUIRE_PIN',
  REQUIRE_ID = 'REQUIRE_ID',
  ONLY_TO_RECIPIENT = 'ONLY_TO_RECIPIENT',
}

interface Verifications {
  requireSignature?: boolean;
  minimumAgeCheck?: number;
  confirmCustomerReceipt?: boolean;
  deliveryVerification: DeliveryVerification;
}

interface PerformedVerifications {
  signatureVerified: boolean;
  confirmationPhoto: boolean;
  idCheckName?: string;
  ageVerified?: number;
  customerConfirmedReceptionAt?: string;
}

type ParcelConstraints = {
  temperature?: TemperatureConstraints;
};

type TemperatureConstraints = {
  maximumCelsius: number;
  minimumCelsius: number;
};

export interface Adressable {
  name: string;
  address: Address;
  email: string;
  phoneCountryCode?: string;
  phoneNumber: string;
}

interface Destination {
  name: string;
  address: Address;
  email: string;
  phoneCountryCode?: string;
  phoneNumber: string;
  bestAvailableWindow?: boolean;
  deliveryWindow: Window;
  verifications: Verifications;
}

export interface Location {
  latitude: number;
  longitude: number;
}

export interface DeliveryConfirmedLocation {
  latitude: string;
  longitude: string;
}

export interface Error {
  message: string;
  propertyPath: string;
}

export interface HubSortInstruction {
  orderPresent: boolean;
  shelfId?: string;
  orderRegion?: Region;
  orderStatus?: OrderStatus;
  geoZone?: GeoZone;
  temperatureControlled?: boolean;
}

export interface ParcelScan {
  scanType: ScanType;
  scannedAt: string;
  scannedBy: string;
  parcelId?: string;
  parcelShipmentIdentifier?: string;
  shelfId?: string;
  confirmed: boolean;
  scannedSize?: string;
}

enum DeliveryCommunicationEventType {
  SMS_RECIPIENT_NEXT_DELIVERY = 'SMS_RECIPIENT_NEXT_DELIVERY',
  SMS_RECIPIENT_ORDERS_DELIVERED = 'SMS_RECIPIENT_ORDERS_DELIVERED',
  SMS_RECIPIENT_ORDERS_PICKED_UP = 'SMS_RECIPIENT_ORDERS_PICKED_UP',
}

export interface DeliveryCommunicationEvent {
  eventType: DeliveryCommunicationEventType;
  time: string;
}

export enum PhotoRating {
  BAD = 'BAD',
  GOOD = 'GOOD',
  VERY_GOOD = 'VERY_GOOD',
}

export interface Delivery {
  id: string;
  recipientEmail: string;
  token: string;
  chatroomToken: string;
  deliveryPin: string;
  location: Location;
  deliveryPinVerifiedOnline?: boolean;
  deliveryLocationVerified: boolean;
  deliveryCommunicationEvents: DeliveryCommunicationEvent[];
  confirmedContactless: boolean;
  photoRating?: PhotoRating;
  userInstructions: UserInstructionsType;
  deliveryRating: number;
  feedback?: string;
  feedbackFlags: string[];
  customerConfirmedReceptionAt?: string;
  verifications: Verifications;
  performedVerifications: PerformedVerifications;
  contactlessAllowed: boolean;
}

export type UserInstructionsType = {
  floor: number | null;
  doorbellName: string | null;
  notificationType: NotificationType | null;
  residenceType: ResidenceType | null;
  note: string | null;
  packagePlaceInformation: string | null;
};

export type ResidenceType = 'HOUSE' | 'APARTMENT';

export type NotificationType =
  | 'DOORBELL'
  | 'KNOCK'
  | 'CALL'
  | 'SMS'
  | 'NO_NOTIFICATION';

export interface OrderPredictionDataset {
  deliveryDay: string;
  region: Region;
  orderCount: number;
  courierCount: number;
  averageRating?: number;
  parcelCount: number;
}

export interface InboundListOrder {
  orderId: string;
  packagesNumber: number;
  weightGrams: number;
  volumeCm3: number;
  packageSize: OrderPackageSize;
  senderName?: string;
  recipientAddress: string;
  recipientName: string;
  deliveryInterval: Interval;
  inboundRegion: Region;
  isHubOrder: boolean;
  xxl: boolean;
  temperatureControlled: boolean;
}

export interface ForwardedOrderInfo {
  orderId: string;
  accountName: string;
  pickupName: string;
  recipientName: string;
  recipientEmail: string;
  recipientPhoneCountryCode?: string;
  recipientPhoneNumber?: string;
  recipientAddress: Address;
  deliverFrom?: string;
  deliverUntil?: string;
  requestReceivedAt: string;
  orderReference?: string;
  consolidated: boolean;
  bestAvailable: boolean;
  parcelCount: number;
}

export interface OrderLabelResponse {
  shipmentLabelUrl: string;
}

export interface OrderUpdateShipmentResponse {}
